import {getCurrentCountryCode, getDomain} from "./s2k-i18nInfoHelper";
import {encodeArrayBufferBase64} from "../src-common/utils/s2k-encodeArrayBufferBase64";
import {createUniqId} from "../src-common/utils/s2k-uuidHelper";
import {NETWORK_STATUS_CODE, STK_APP_NAME_PREVIEW_SEND, STK_APP_NAME_QUICK_SEND} from "../src-common/constants";

// ######## Public Methods ########
/**
 * Handle an XMLHttpRequest.
 * @param {{
 * url: string,
 * path: string,
 * type?: {"GET", "PUT", "POST"},
 * headers: {},
 * data:{},
 * timeout: number,
 * cache: boolean,
 * binary: boolean,
 * responseType: string,
 * dataType: string,
 * isUploadToS3: boolean,
 * isDownloadBinary: boolean,
 * isJSONContentType: boolean,
 * isCsrfRequired: boolean
 * }} request Settings for the AJAX request
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: (any| string| ArrayBuffer), contentType: string | undefined }>}
 */
async function ajax(request) {
    // Validate the input data.
    let ajaxRequest = request || {};
    ajaxRequest.url = request.url || "";
    ajaxRequest.path = request.path || "";
    ajaxRequest.type = request.type || "GET";
    ajaxRequest.data = request.data || undefined;
    ajaxRequest.completion = request.completion || function () {
    };
    ajaxRequest.timeout = request.timeout || 30000;
    ajaxRequest.cache = request.cache || false;
    ajaxRequest.binary = request.binary || false;
    ajaxRequest.responseType = request.responseType || "";
    ajaxRequest.dataType = request.dataType || "text";
    ajaxRequest.isUploadToS3 = request.isUploadToS3 || false;
    ajaxRequest.isDownloadBinary = request.isDownloadBinary || false;
    ajaxRequest.isJSONContentType = request.isJSONContentType || false;
    ajaxRequest.isCsrfRequired = request.isCsrfRequired || false;

    if (ajaxRequest.data && ajaxRequest.binary === false) {
        if (ajaxRequest.isJSONContentType) {
            ajaxRequest.data = JSON.stringify(ajaxRequest.data);
        } else {
            ajaxRequest.data = $.param(ajaxRequest.data);
        }
    }

    // Config headers of request
    ajaxRequest.headers = new Headers();

    if (ajaxRequest.isUploadToS3) {
        ajaxRequest.headers.append("Content-Type", "");
    } else if (ajaxRequest.isDownloadBinary) {
        ajaxRequest.headers.append("Content-Type", "application/x-www-form-urlencoded");
    }

    if (ajaxRequest.data  && !ajaxRequest.binary) {
        if (ajaxRequest.isJSONContentType) {
            ajaxRequest.headers.append("Content-Type", "application/json");
            ajaxRequest.headers.append("Accept", "application/json");
        } else {
            ajaxRequest.headers.append("Content-Type", "application/x-www-form-urlencoded");
        }
    }

    if (!request.cache) {
        ajaxRequest.headers.append("If-Modified-Since", new Date().toLocaleString());
    }
    if (request.isCsrfRequired) {
        let res = await getOrRefreshCsrfToken();
        if (res.success) {
            ajaxRequest.headers.append("anti-csrftoken-a2z", res.data.token);
        } else {
            return res;
        }
    }

    // Config url based on current domain
    if (ajaxRequest.url === "") {
        let baseUrl = await getBaseUrl();
        ajaxRequest.url = baseUrl + request.path;
    }

    return await makeRequest(ajaxRequest);
}

/**
 * Get Base Url of current country
 * @return {baseUrl: Promise<string>} sendResponse A callback func to pass the base Url
 */
async function getBaseUrl() {
    return await getDomain() + "/sendtokindle";
}

/**
 * Handle an XMLHttpRequest.
 * @param {{
 * url: string,
 * path: string,
 * headers: {},
 * type: {GET, PUT, POST},
 * data: {},
 * timeout: number,
 * cache: boolean,
 * binary: boolean,
 * responseType: string,
 * dataType: string,
 * isUploadToS3: boolean,
 * isDownloadBinary: boolean,
 * isJSONContentType: boolean,
 * isCsrfRequired: boolean}
 * |{}} request Settings for the AJAX request
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: (any| string| ArrayBuffer), contentType: string | undefined}>}
 */
async function makeRequest(request) {
    // Check for Internet connectivity
    if (!await isOnline()) {
        return {
            success: false,
            statusCode: NETWORK_STATUS_CODE.NO_NETWORK,
            statusText: "no network",
            data: undefined
        };
    }
    // Need to preserve value throughout the promise chain
    let responseContentType;
    // Set time out
    const controller = new AbortController();
    let timeoutId = setTimeout(function () {
        // Stop request
        controller.abort();
    }, request.timeout);
    // Send request
    try {
        let response = await fetch(request.url,
            {
                method: request.type,
                body: request.data,
                headers: request.headers,
                signal: controller.signal
            });
        // Clear the timeout when the response is received
        clearTimeout(timeoutId);
        // If the response is not ok, return request error
        if (!response.ok) {
            return {
                success: false,
                statusCode: NETWORK_STATUS_CODE.GENERAL_FAIL,
                statusText: response.statusText,
                data: undefined
            };
        } else {
            responseContentType = response.headers.get("Content-Type");
            let data;
            // Resolve data from response based on the response type
            if (request.responseType === "arraybuffer") {
                data = await response.arrayBuffer();
            } else if (request.responseType === "json" || (responseContentType && responseContentType.startsWith("application/json"))) {
                try {
                    data = await response.json();
                } catch (e) {
                    console.log("request error: url=" + request.url + ";  param = " + request.data + "exception = " + e);
                    return {
                        success: false,
                        statusCode: NETWORK_STATUS_CODE.DATA_RETRIEVE_FAIL,
                        statusText: "Data fail",
                        data: undefined,
                        contentType: responseContentType
                    };
                }
            } else {
                data = await response.text();
            }
            return {
                success: true,
                statusCode: NETWORK_STATUS_CODE.SUCCESS,
                statusText: "success",
                data: request.isDownloadBinary ? encodeArrayBufferBase64(data) : data,
                contentType: responseContentType
            };
        }
    } catch (e) {
        if (e.name === "AbortError") {
            // Request is aborted due to timeout
            return {
                success: false,
                statusCode: NETWORK_STATUS_CODE.TIMEOUT,
                statusText: "timeout",
                data: undefined
            };
        } else {
            // Other error
            return {
                success: false,
                statusCode: NETWORK_STATUS_CODE.GENERAL_FAIL,
                statusText: "fail",
                data: undefined
            };
        }
    }
}

/**
 * Test if customer is connected to network
 * @return {Promise<boolean>}
 */
async function isOnline() {
    // Function provide by browser, but sometimes not trustable
    if (!navigator.onLine) {
        return false;
    } else {
        // Try to call base domain, there's no network if an error occur
        try {
            await fetch(await getDomain(), {method: 'HEAD'});
            return true;
        } catch (error) {
            return false;
        }
    }
}

/**
 * Request page content
 * @param {string} url - Url for page to request
 * @return {Promise<{success: boolean, statusCode: number, statusText: string, data: (*|string|ArrayBuffer), contentType: (string|undefined)}>}
 */
async function requestPageContent(url) {
    return await ajax({
        url: url,
        type: "GET",
        dataType: "html",
        timeout: 5000,
    });
}


/**
 * retrieve csrf token from /empty page's csrfToken tag.
 * The /empty page is specifically designed for extension to retrieve tag by
 *      classname['a-container'] -> tagname['input'] -> value
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: { token: string }, contentType: string | undefined}>}
 */
async function getOrRefreshCsrfToken() {
    // Check if there's a cache of CSRF token
    const {s2kGUID} = await chrome.storage.local.get("s2kGUID");
    const csrfStorageKey = `${s2kGUID}csrf_token`;
    const csrfTimeKey = `${s2kGUID}csrf_time`;
    const now = new Date().getTime() / 1000;
    let {
        [csrfStorageKey]: token,
        [csrfTimeKey]: issueTime
    } = await chrome.storage.local.get([csrfStorageKey, csrfTimeKey]);
    if (token && issueTime) {
        issueTime = parseInt(issueTime);
        // CSRF token in 60s is valid
        if (now - issueTime < 60) {
            return {
                success: true,
                statusCode: NETWORK_STATUS_CODE.SUCCESS,
                statusText: "Success",
                data: {
                    'token': token
                },
            };
        }
    }

    // Existing token is expired or non-existent, we'll request a new one
    let res = await ajax({
        type: "GET",
        path: "/empty",
        cache: false,
        timeout: 15000,
    });
    if (!res.success) {
        res.data = undefined;
        console.log("getOrRefreshCsrfToken network error");
    } else {
        token = /name='csrfToken' value='(.*)' \/>/gm.exec(res.data)[1];
        if (!token) {
            res.data = undefined;
            console.log("getOrRefreshCsrfToken fail to extract");
        } else {
            res.data = {
                'token': token
            };
            // Compute the new token's expiry time and commit to storage
            issueTime = new Date().getTime() / 1000;
            chrome.storage.local.set({
                [csrfStorageKey]: token,
                [csrfTimeKey]: issueTime
            });
        }
    }
    return res;
}

/**
 * Request WebService to check authentication status
 * @param { boolean } useCache A bool value to show if the func need use cache
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: {isAuthed: boolean, guid:string}, contentType: string | undefined}>}
 */
async function checkAuth(useCache) {
    let {
        ["s2kAuthStatus"]: s2kAuthStatus,
        ["s2kGUID"]: s2kGUID
    } = await chrome.storage.local.get(["s2kAuthStatus", "s2kGUID"]);

    // The cache is used for loading main UI of pop up, to prevent user from waiting long time for the result of request.
    // So, it only worked if the user is logged in before.
    if (useCache) {
        return {
            success: true,
            statusCode: NETWORK_STATUS_CODE.SUCCESS,
            statusText: "success",
            data: {
                isAuthed: s2kAuthStatus || false,
                guid: s2kGUID
            }
        };
    } else {
        let res = await ajax({
            type: "GET",
            path: "/extension/checkAuth",
            cache: false,
            timeout: 15000
        });
        if (res.success || res.data ) {
            if (res.data.guid !== s2kGUID) {
                // If the user is different from last logged-in user, treat them as unAuthed
                res.data = {
                    isAuthed: false,
                    guid: res.data.guid
                };
            } else {
                // Store auth status and GUID into local storage
                res.data = {
                    isAuthed: res.data.isAuthed,
                    guid: res.data.guid
                };
            }
        } else {
            console.error("Auth check fail errorCode: " + res.statusCode + " errorText: " + res.statusText);
            res.data = {
                isAuthed: false,
                guid: undefined
            };
        }
        return res;
    }
}

/**
 * retrieve username from /empty page's navigation bar.
 * The /empty page is specifically designed for extension to retrieve username by
 *      'config.customerName','username' -> username
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: {username: username}, contentType: string | undefined}>}
 */
async function getAndUpdateUsername() {
    // Request `/empty` page
    let res = await ajax({
        type: "GET",
        path: "/empty",
        cache: false,
        timeout: 15000,
    });
    // Default username is `Unknown`
    let username = "Unknown";
    if (res.success) {
        let regex = /Nav\.declare\('config\.customerName','(.+)'/;
        let match = res.data.match(regex);
        if (match && match.length > 0 && match[1] && match[1] !== "") {
            username = match[1];
        }

        // Decode the username since retail will encode it (because it is like \\x plus something), we need to remove those \\x at first
        let unescapedString = username.replace(/\\x/g, '%');
        // Change \u to %u for recognize Unicode
        let escapedString = unescapedString.replace(/\\u([\d\w]{4})/gi, function (match, grp) {
            return String.fromCharCode(parseInt(grp, 16));
        });
        if (escapedString ) {
            username = decodeURIComponent(escapedString);
        }
        res.data = {
            'username': username
        };
        chrome.storage.local.set({
            "s2kUsername": username,
        });
    } else {
        res.data = {
            'username': 'Unknown'
        };
    }
    return res;
}

/**
 * Get Auth related Url of current country
 * @param {boolean} useCache A bool value to show if the func need use cache
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: { registerUrl: string, signInUrl: string, passwordUrl: string }, contentType: string | undefined}>}
 */
async function getAuthRelatedUrls(useCache) {
    let cacheKey = await getCurrentCountryCode() + "authRelatedUrls";
    let {[cacheKey]: cache} = await chrome.storage.local.get([cacheKey]);
    if (useCache && cache ) {
        // Use cache if it is allowed and not undefined
        return {
            success: true,
            statusCode: NETWORK_STATUS_CODE.SUCCESS,
            statusText: "success",
            data: cache
        };
    } else {
        let res = await ajax({
            type: "GET",
            dataType: "json",
            path: "/extension/getAuthPortalUrl",
            cache: false,
            timeout: 15000
        });
        // Cache need to distinguish country.
        if (res.success && res.data ) {
            // Response of getAuthPortalUrl has 3 property - signInUrl, passwordUrl and registerUrl
            await chrome.storage.local.set({[cacheKey]: res.data});
        } else if (cache ) {
            res.data = cache;
        } else {
            res.data = {
                registerUrl: undefined,
                signInUrl: undefined,
                passwordUrl: undefined
            };
        }
        return res;
    }
}

/**
 * Request devices list of current user
 * @return {Promise<Array>} Return devices list
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: {ownedDevices: Array}, contentType: string | undefined}>}
 */
async function requestDevicesList() {
    let res = await ajax({
        type: "POST",
        dataType: "json",
        path: "/get-device-list",
        isJSONContentType: true,
        isCsrfRequired: true,
        cache: false,
        timeout: 15000,
        data: {
            extName: STK_APP_NAME_QUICK_SEND,
            extVersion: "1.0",
        }
    });
    if (res.success || res.data ) {
        // Will return the devices list (return empty list if the request is success but no devices info)
        res.data = {
            ownedDevices: res.data.ownedDevices || []
        };
    } else {
        res.data = {
            ownedDevices: []
        };
    }
    return res;
}

/**
 * Download content in binary format.
 * @param url       URL
 * @param success   Success Callback
 * @param error     Error Callback
 */
function downloadAsBinary(url, success, error) {
    ajax({
        url: url,
        cache: true,
        responseType: "arraybuffer",
        isDownloadBinary: true,
        timeout: 15000,
    }).then(function (res) {
        if (res.success) {
            if (!res.contentType) { // try to fix content type if null
                let ext = url.split('.').pop().toLowerCase();
                if (ext === "jpg" || ext === "jpeg") {
                    res.contentType = "image/jpeg";
                } else if (ext === "png") {
                    res.contentType = "image/png";
                } else if (ext === "gif") {
                    res.contentType = "image/gif";
                }
            }
            success(res.data, res.contentType);
        } else {
            if (error ) {
                error();
            }
        }
    });
}

/**
 * Call `web-extract` API to get a EPUB file of current webpage.
 *
 * @param { string } url
 * @param { string } content
 * @param { string } extractionID An unique id to identify the extraction to connect extraction with the whole work flow
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: { needLocalExtract: boolean, extractionResult: {statusCode: string, message:string, epubString: string}}, contentType: string | undefined}>}
 */
async function s2kWebExtract (url, content, extractionID) {
    let res = await ajax({
        type: "POST",
        dataType: "json",
        path: "/web-extract",
        isJSONContentType: true,
        isCsrfRequired: true,
        cache: false,
        timeout: 30000,
        data: {
            url: url,
            content: content,
            extractionID: extractionID
        }
    });

    let result = {};
    result.statusText = res.statusText;
    if (!res.success) {
        result.success = false;
        result.statusCode = res.statusCode || NETWORK_STATUS_CODE.GENERAL_FAIL;
        result.data = undefined;
    } else if (!res.data){
        result.success = false;
        result.statusCode = NETWORK_STATUS_CODE.DATA_RETRIEVE_FAIL;
        result.data = undefined;
    } else {
        result.success = true;
        const {needLocalExtract, extractionResult} = res.data;
        // If no need to local extract but do not have extraction result, it's a service error
        if (!needLocalExtract && !extractionResult) {
            result.success = false;
            result.data = undefined;
            result.statusCode = NETWORK_STATUS_CODE.GENERAL_FAIL;
        } else {
            result.statusCode = NETWORK_STATUS_CODE.SUCCESS;
            result.data = res.data;
        }
    }
    return result;
}

/**
 * Call init API to get upload Url
 *
 * @param {"OCS" | "Preview"} context
 * @param { number } fileSize
 * @param { string } fileType
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: { uploadUrl: string, stkToken: string }, contentType: string | undefined}>}
 */
async function sendToKindleInit(context, fileSize, fileType) {
    let res = await ajax({
        type: "POST",
        dataType: "json",
        path: "/init",
        isJSONContentType: true,
        isCsrfRequired: true,
        cache: false,
        timeout: 15000,
        data: {
            "extName": context === "OCS" ? STK_APP_NAME_QUICK_SEND : STK_APP_NAME_PREVIEW_SEND,
            appVersion: BUILD_PROPS.__version__,
            fileSize: fileSize,
            fileExtension: fileType || "html"
        }
    });

    if (!res.success) {
        res.data = undefined;
    } else {
        const {stkToken, uploadUrl} = res.data;
        if (!stkToken || !uploadUrl) {
            res.success = false;
            res.data = undefined;
            res.statusCode = NETWORK_STATUS_CODE.DATA_RETRIEVE_FAIL;
        } else {
            res.data = {
                'stkToken': stkToken,
                'uploadUrl': uploadUrl
            };
        }
    }
    return res;
}

/**
 * Uploads the document to S3
 *
 * @param {"OCS" | "Preview"} context
 * @param {string} uploadUrl Pre-signed S3 URL returned by init API
 * @param data File to upload
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: {}, contentType: string | undefined}>} */
async function uploadToS3(uploadUrl, data, context) {
    return await ajax({
        url: uploadUrl,
        type: "PUT",
        data: data,
        cache: true,
        binary: true,
        isUploadToS3: true,
        timeout: 15000,
    });
}

/**
 * Upload feedback
 *
 * @param {string} feedbackText The feedback provided by customer
 * @param {string} report The report contains rating and platform info.
 */
function uploadFeedback(feedbackText, report) {
    // Call webservice to submit feedback
    ajax({
        path: "/feedback",
        type: "POST",
        data: {
            "type": "feedback",
            "appType": "SendToKindleChrome",
            "content": feedbackText,
            "report": report
        },
        isJSONContentType: true,
        isCsrfRequired: true,
    }).then();
}

/**
 * Call proxy service to trigger send workflow
 * @param {"OCS" | "Preview"} context
 * @param {string} stkToken
 * @param {number} fileSize
 * @param {boolean} archive
 * @param {Array<string>} deviceList
 * @param {{
 *   formattedContent: string,
 *   title: string,
 *   dataType: string,
 *   fileType: string,
 *   epubString: string,
 *   author?: string,
 *   source?: string,
 *   extractionID?: string,
 * }} documentMetadata
 *
 * @return {Promise<{ success: boolean, statusCode: number, statusText: string, data: {}, contentType: string | undefined}>}
 */
async function sendToKindle(context, stkToken, fileSize, archive, deviceList, documentMetadata) {
    let data = {
        "extName": context === "OCS" ? STK_APP_NAME_QUICK_SEND : STK_APP_NAME_PREVIEW_SEND,
        "extVersion": BUILD_PROPS.__version__,
        "inputFormat": documentMetadata.fileType,
        "stkToken": stkToken,
        "title": documentMetadata.title,
        "dataType": documentMetadata.dataType,
        "archive": archive,
        "deviceList": archive ? [] : deviceList,
        "fileSize": fileSize,
        "inputFileName": documentMetadata.title + "." + documentMetadata.fileType,
        "batchId": createUniqId()
    };
    if (documentMetadata.author && documentMetadata.author !== "") {
        data.author = documentMetadata.author;
    }
    if (documentMetadata.extractionID && documentMetadata.extractionID !== "") {
        data.extractionID = documentMetadata.extractionID;
    }
    return await ajax({
        type: "POST",
        dataType: "json",
        path: "/send-v2",
        isJSONContentType: true,
        isCsrfRequired: true,
        data: data
    });
}

/**
 * Send preview metrics to remote service.
 * @param {MetricsBody[]} metrics Metrics
 * @
 */
async function emitMetrics(metrics) {
    let path = "/dnd_metrics";
    let res = await ajax({
        type: "POST",
        path: path,
        dataType: "json",
        isJSONContentType: true,
        isCsrfRequired: true,
        cache: false,
        timeout: 10000,
        data: {
            "clientName": "Chrome",
            "metrics": metrics
        },
    });

    return res.success;
}

/**
 * Record domain that user send/preview/review-send
 * @param {"send" | "preview" | "pre-s" | "error" | "timeout"} operation Operation name
 * @param {String} domain Domain name
 */
async function reportDomain(operation, domain) {
    let path = "/extension/recordDomain";
    let res = await ajax({
        type: "POST",
        path: path,
        dataType: "json",
        isJSONContentType: true,
        isCsrfRequired: true,
        cache: false,
        timeout: 10000,
        data: {
            "operationName": operation,
            "domain": domain
        },
    });

    return res.success;
}

/**
 * Record the rating and the url that user rating is lower than 2
 * @param {number} rating Customer rating
 * @param {string} url Domain name
 */
async function reportLowRating(rating, url) {
    let path = "/extension/recordLowRatingUrl";
    let res = await ajax({
        type: "POST",
        path: path,
        dataType: "json",
        isJSONContentType: true,
        isCsrfRequired: true,
        cache: false,
        timeout: 10000,
        data: {
            "rating": rating,
            "url": url
        },
    });

    return res.success;
}

export {
    checkAuth,
    getAndUpdateUsername,
    requestDevicesList,
    requestPageContent,
    getAuthRelatedUrls,
    s2kWebExtract,
    sendToKindleInit,
    uploadToS3,
    sendToKindle,
    downloadAsBinary,
    uploadFeedback,
    emitMetrics,
    reportDomain,
    reportLowRating
};